import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { emit } from '../../platform/analytics/analytics'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
import type { Category } from './categoryTree'

export type CategoryCreate = components['schemas']['CategoryCreate']
export type CategoryUpdate = components['schemas']['CategoryUpdate']

/**
 * Distinguishable in a log from a render-time failure.
 *
 * Carries the HTTP `status`, not just the parsed body: `openapi-fetch`'s
 * `error` is the response BODY, which has no status field of its own — the
 * status is on the sibling `response`. Task 9 needs to tell a 404 apart from
 * a 400, so the status is captured here, at the one place every read and
 * write in this module throws, rather than retrofitted once a caller needs
 * it.
 */
export class CategoriesError extends Error {
  readonly status?: number

  constructor(cause: unknown, status?: number) {
    super('categories-unavailable', { cause })
    this.name = 'CategoriesError'
    this.status = status
  }
}

export type CategoryList = components['schemas']['CategoryList']

/**
 * The WHOLE response is cached, not `data.categories`.
 *
 * It used to return the array and drop everything around it. Backend 270 put
 * things in that discarded envelope that are not derivable from the rows —
 * `most_used` and `region` today; `icon_view_box` and `icon_fill_rule` too,
 * until backend #401 moved each category's icon to its own `icon_url`. The
 * alternative, a second query for the metadata, would be a second request for
 * a response we already have.
 *
 * Every hook below is a `select` over this one entry, which lets a second
 * observer read its own slice without a second fetch — `usePalette` was the
 * first of these before #454 removed category colour, and its own `select`
 * with it.
 */
async function listCategories(): Promise<CategoryList> {
  const { data, error, response } = await api.GET('/api/v1/categories')
  if (error !== undefined || data === undefined) throw new CategoriesError(error, response.status)
  return data
}

/** One key, one fetcher, paired here so two observers cannot drift onto
 *  different keys — which would be two fetches of the same 285 rows. */
const listQuery = { queryKey: keys.categories.list(), queryFn: listCategories } as const

/** Hoisted, not inline. TanStack memoises a `select` result by the identity of
 *  the function as well as the data, so an arrow rebuilt every render defeats
 *  it and hands every consumer a new array on each pass. */
const selectCategories = (list: CategoryList) => list.categories

/**
 * Four states, not five. Per D20 categories have no upstream sync, so no `asOf`
 * is passed and the stale branch is unreachable — `Async` throws if it appears.
 *
 * **No `isEmpty`.** 88 system categories are seeded with `user_id = NULL` and
 * returned to every user, so an empty response means the API is broken rather
 * than that the user has nothing. Rendering "no categories yet" would explain a
 * server fault as a user state.
 */
export function useCategories(): AsyncState<Category[]> {
  const query = useQuery({ ...listQuery, select: selectCategories })
  return fromQuery(query, {
    message: () => 'Categories could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The caller's most-tagged categories, most first, at most 8 — category **ids**,
 * because a user's own categories appear here and those have no `key`.
 *
 * Empty for a caller with no transactions, and empty on a backend that predates
 * the field. Both mean "no ranking available", and every consumer has to have
 * an answer for that case anyway.
 */
/** One shared empty, not a fresh `[]` per call — a new array reference every
 *  render is a new identity for every `useMemo` and `useEffect` downstream. */
const NO_RANKING: string[] = []

const selectMostUsed = (list: CategoryList) => list.most_used ?? NO_RANKING

export function useMostUsed(): string[] {
  const { data } = useQuery({ ...listQuery, select: selectMostUsed })
  return data ?? NO_RANKING
}

/**
 * Invalidates `categories/list` — the narrowest key covering what changed. The
 * new row, the total and the palette all come from that one response.
 *
 * The `response` sibling of `openapi-fetch`'s result, not just `error`, is
 * what carries the HTTP status — same reasoning `CategoriesError` already
 * documents for `listCategories`. Task 9's edit path is not the first caller
 * to need the status; the collision check on a 400 is the first.
 */
export function useCreateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: CategoryCreate) => {
      const { data, error, response } = await api.POST('/api/v1/categories', { body })
      if (error !== undefined || data === undefined) throw new CategoriesError(error, response.status)
      return data
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories.list() }),
  })
}

/**
 * `PATCH` accepts name, icon, colour, description and type — and NOT
 * `parent_id` or `display_order`. Re-parenting and reordering have no route on
 * any client, so the form must not offer either.
 *
 * A **404** here means "not yours", not "gone": both write handlers select on
 * `user_id` before checking `is_system`, so a system category never reaches
 * the 403 branch. Treating it as a deletion elsewhere and refetching would be
 * wrong.
 *
 * The plan's own sketch of this mutation dropped `response` and passed only
 * `error` to `CategoriesError` — which would leave `.status` `undefined` on
 * every failure and make the 404 branch above unreachable. Captured here the
 * same way `useCreateCategory` and `listCategories` already do.
 */
export function useUpdateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: CategoryUpdate }) => {
      const { data, error, response } = await api.PATCH('/api/v1/categories/{category_id}', {
        params: { path: { category_id: id } },
        body,
      })
      if (error !== undefined || data === undefined) throw new CategoriesError(error, response.status)
      return data
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories.list() }),
  })
}

/**
 * `DELETE /categories/{id}?reassign_to=` (#285, backend #478). One database
 * transaction: the user's transactions and recurring streams move to
 * `reassignTo` and the children go to the TOP LEVEL, then the category is
 * soft-deleted.
 *
 * `reassignTo: null` OMITS the param — the transactions become Uncategorized
 * and stay open to rules. An explicit target sends it, and the moved rows are
 * locked against rules.
 *
 * Invalidates every cache that holds a moved row: the category list, all
 * transactions (lists, review count, stats), recurring, and the review inbox
 * (its own root, outside `transactions`). Nothing is optimistic, so a failure
 * has nothing to roll back.
 *
 * A **404** is "not yours", not "gone", for the reason `useUpdateCategory`
 * documents. A **422** is a rejected target.
 */
export function useDeleteCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ category, reassignTo }: { category: Category; reassignTo: string | null }) => {
      const id = category.id
      const { error, response } = await api.DELETE('/api/v1/categories/{category_id}', {
        params: {
          path: { category_id: id },
          ...(reassignTo === null ? {} : { query: { reassign_to: reassignTo } }),
        },
      })
      // No `data` check: a 204 carries no body, so `data === undefined` is the
      // SUCCESS case here.
      if (error !== undefined) throw new CategoriesError(error, response.status)
    },
    // Returned, so `mutateAsync` resolves only once the list has refetched
    // without the row: the dialog closes on a list that no longer shows it.
    onSuccess: (_data, { category }) => {
      // Booleans derived from the deleted row, like create and edit (rule 7: no name, no id).
      emit('category_deleted', {
        custom_color_used: category.color != null,
        custom_icon_used: category.icon != null,
      })
      void client.invalidateQueries({ queryKey: keys.transactions.all() })
      void client.invalidateQueries({ queryKey: keys.cashflow.all() })
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
      void client.invalidateQueries({ queryKey: keys.review.all() })
      return client.invalidateQueries({ queryKey: keys.categories.list() })
    },
  })
}
